import { useState, useRef, useCallback, useEffect, ReactNode } from 'react';
import { GripVertical } from 'lucide-react';

interface ResizablePanelProps {
  children: ReactNode;
  /** Largeur minimale en pixels */
  minWidth?: number;
  /** Largeur maximale en pixels */
  maxWidth?: number;
  /** Largeur initiale en pixels */
  defaultWidth?: number;
  /** Classe CSS additionnelle */
  className?: string;
  /** Callback appelé lors du redimensionnement */
  onResize?: (width: number) => void;
  /** Clé pour persister la largeur dans localStorage */
  storageKey?: string;
}

/**
 * Panneau redimensionnable par drag horizontal
 * Permet de redimensionner la largeur d'un panneau en faisant glisser la bordure droite
 */
export function ResizablePanel({
  children,
  minWidth = 250,
  maxWidth = 600,
  defaultWidth = 350,
  className = '',
  onResize,
  storageKey
}: ResizablePanelProps) {
  // Récupérer la largeur sauvegardée ou utiliser la valeur par défaut
  const getSavedWidth = useCallback(() => {
    if (storageKey) {
      const saved = localStorage.getItem(`resizable-panel-${storageKey}`);
      if (saved) {
        const parsedWidth = parseInt(saved, 10);
        if (!isNaN(parsedWidth) && parsedWidth >= minWidth && parsedWidth <= maxWidth) {
          return parsedWidth;
        }
      }
    }
    return defaultWidth;
  }, [storageKey, minWidth, maxWidth, defaultWidth]);

  const [width, setWidth] = useState(getSavedWidth);
  const [isResizing, setIsResizing] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  // Sauvegarder la largeur dans localStorage
  const saveWidth = useCallback((newWidth: number) => {
    if (storageKey) {
      localStorage.setItem(`resizable-panel-${storageKey}`, String(newWidth));
    }
  }, [storageKey]);

  // Gestionnaire de début de drag
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    startXRef.current = e.clientX;
    startWidthRef.current = width;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [width]);

  // Gestionnaire de mouvement pendant le drag
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isResizing) return;

    const delta = e.clientX - startXRef.current;
    let newWidth = startWidthRef.current + delta;

    // Appliquer les limites min/max
    newWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));

    setWidth(newWidth);
    onResize?.(newWidth);
  }, [isResizing, minWidth, maxWidth, onResize]);

  // Gestionnaire de fin de drag
  const handleMouseUp = useCallback(() => {
    if (isResizing) {
      setIsResizing(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      saveWidth(width);
    }
  }, [isResizing, width, saveWidth]);

  // Gestionnaire tactile pour mobile
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    setIsResizing(true);
    startXRef.current = e.touches[0].clientX;
    startWidthRef.current = width;
  }, [width]);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!isResizing) return;

    const delta = e.touches[0].clientX - startXRef.current;
    let newWidth = startWidthRef.current + delta;
    newWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));

    setWidth(newWidth);
    onResize?.(newWidth);
  }, [isResizing, minWidth, maxWidth, onResize]);

  const handleTouchEnd = useCallback(() => {
    if (isResizing) {
      setIsResizing(false);
      saveWidth(width);
    }
  }, [isResizing, width, saveWidth]);

  // Attacher/détacher les événements globaux
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleTouchEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isResizing, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  // Responsive: sur mobile, utiliser la largeur complète
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024); // lg breakpoint
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Sur mobile, afficher en pleine largeur sans redimensionnement
  if (isMobile) {
    return (
      <div className={`w-full ${className}`}>
        {children}
      </div>
    );
  }

  return (
    <div
      ref={panelRef}
      className={`relative flex-shrink-0 ${className}`}
      style={{ width: `${width}px` }}
    >
      {/* Contenu du panneau */}
      <div className="h-full overflow-hidden">
        {children}
      </div>

      {/* Poignée de redimensionnement */}
      <div
        className={`
          absolute top-0 right-0 h-full w-2 cursor-col-resize
          flex items-center justify-center
          group hover:bg-primary/10 transition-colors
          ${isResizing ? 'bg-primary/20' : ''}
        `}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        role="separator"
        aria-orientation="vertical"
        aria-label="Redimensionner le panneau"
        tabIndex={0}
        onKeyDown={(e) => {
          // Support clavier pour l'accessibilité
          if (e.key === 'ArrowLeft') {
            const newWidth = Math.max(minWidth, width - 20);
            setWidth(newWidth);
            saveWidth(newWidth);
          } else if (e.key === 'ArrowRight') {
            const newWidth = Math.min(maxWidth, width + 20);
            setWidth(newWidth);
            saveWidth(newWidth);
          }
        }}
      >
        {/* Indicateur visuel de la poignée */}
        <div
          className={`
            h-16 w-1 rounded-full transition-all duration-150
            ${isResizing 
              ? 'bg-primary scale-y-110' 
              : 'bg-gray-300 dark:bg-gray-600 group-hover:bg-primary/60 group-hover:scale-y-105'
            }
          `}
        />
        
        {/* Icône grip visible au survol */}
        <GripVertical 
          className={`
            absolute h-4 w-4 text-gray-400 dark:text-gray-500
            opacity-0 group-hover:opacity-100 transition-opacity
            ${isResizing ? 'opacity-100 text-primary' : ''}
          `}
        />
      </div>

      {/* Overlay pendant le redimensionnement pour éviter les interactions indésirables */}
      {isResizing && (
        <div className="fixed inset-0 z-50 cursor-col-resize" />
      )}
    </div>
  );
}

export default ResizablePanel;
